import { HttpClient, HttpContext } from '@angular/common/http';
import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { BYPASS_ACCESSTOKEN } from '@service/interceptors/authentication-interceptor';
import { BYPASS_RETRY } from '@service/interceptors/retryInterceptor';
import { LoggerService } from '@service/loggers/logger.service';
import { BehaviorSubject, from, fromEvent, interval, merge, of, Subject, Subscription, timer } from 'rxjs';
import { map, mapTo, switchMap, take, takeUntil } from 'rxjs/operators';


export const HEART_BEAT_URL = 'https://apis.tunify.com/t4-zone-service'; //needs to be an url that supports CORS

export const HEART_BEAT_INTERVAL_WHEN_INTERNET_UP_MS = 300000; //every 5 minutes

export const HEART_BEAT_INTERVAL_WHEN_INTERNET_DOWN__EXPONENTIAL_BACKOFF_BASE_MS = 4000; //start with 4 seconds delay
export const HEART_BEAT_INTERVAL_WHEN_INTERNET_DOWN__EXPONENTIAL_BACKOFF_MAX_MS = 60000; //every 60 seconds


@Injectable({
  providedIn: 'root',

})
export class InternetConnectionStatusService {

    private static LOGGER_CLASSNAME = InternetConnectionStatusService.name;

    private get _hasNetwork(): boolean {
      return this._hasNetworkSubject.value;
    }

    private set _hasNetwork(value: boolean) {
      if (this._hasNetwork !== value) {
        this._hasNetworkSubject.next(value);
      }
    }
    get hasNetwork(): boolean {
      return this._hasNetwork;
    }
    private _hasNetworkSubject = new BehaviorSubject<boolean>(false);
    public hasNetwork$ = this._hasNetworkSubject.asObservable();



    private get _hasInternetAccess(): boolean {
      return this._hasInternetAccessSubject.value;
    }

    private set _hasInternetAccess(value: boolean) {
      if (this._hasInternetAccess !== value) {
          this._hasInternetAccessSubject.next(value);
      }
    }
    get hasInternetAccess(): boolean {
      return this._hasInternetAccess;
    }
    private _hasInternetAccessSubject = new BehaviorSubject<boolean>(false);
    public hasInternetAccess$ = this._hasInternetAccessSubject.asObservable();

    public checkInternetNow(){
      this.checkInternetAccess();
    }

    constructor(
      private httpClient: HttpClient,
      private loggerService: LoggerService,
    ) {
        this.setupNetworkStateCheckers();
        this.checkInternetAccess();

        this.hasNetwork$.pipe(
          takeUntil(this.destroyed$)
        ).subscribe((hasNetwork) => {
          this.loggerService.debug(InternetConnectionStatusService.LOGGER_CLASSNAME, "constructor", "Network state changed: " + hasNetwork);
          if (hasNetwork) {
            //network is back -> check internet access immediately
            this.noInternetAmountsAfterChange = 0;
            this.checkInternetAccess();
          } else {
            //no network -> no internet access possible
            this._hasInternetAccess = false;
          }
        });
    }

    private setupNetworkStateCheckers() {
      this._hasNetwork = navigator.onLine;

      fromEvent(window, 'online')
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this._hasNetwork = true;
      });

      fromEvent(window, 'offline')
      .pipe(
        takeUntil(this.destroyed$)
      )
      .subscribe(() => {
        this._hasNetwork = false;
      });
    }


    private scheduleNextInternetCheckStarts$ = new Subject<void>();
    private scheduleNextInternetCheck() {

      this.scheduleNextInternetCheckStarts$.next();

      var timeout : number = null;
      if (this._hasInternetAccess) {
        timeout = HEART_BEAT_INTERVAL_WHEN_INTERNET_UP_MS;
      } else if(this._hasNetwork) {
        timeout = Math.min(2 ** this.noInternetAmountsAfterChange * HEART_BEAT_INTERVAL_WHEN_INTERNET_DOWN__EXPONENTIAL_BACKOFF_BASE_MS, HEART_BEAT_INTERVAL_WHEN_INTERNET_DOWN__EXPONENTIAL_BACKOFF_MAX_MS);
      } else {
        this.loggerService.debug(InternetConnectionStatusService.LOGGER_CLASSNAME, "scheduleNextInternetCheck", "No network, no need to check internet access");
      }

      if (timeout) {
        timer(timeout)
        .pipe(
          takeUntil(
            merge(
              this.destroyed$,
              this.scheduleNextInternetCheckStarts$,
              this.internetCheckStarts$
            )
          )
        ).subscribe(() => {
          this.checkInternetAccess();
        }
        );
      }
    }

    private internetCheckStarts$ = new Subject<void>();
    private noInternetAmountsAfterChange = 0;
    private checkInternetAccess() {
      this.internetCheckStarts$.next();

      //this.httpClient.get("https://www.google.com", { context: new HttpContext().set(BYPASS_ACCESSTOKEN, true).set(BYPASS_RETRY, true) })
      this.httpClient.get(HEART_BEAT_URL, { context: new HttpContext().set(BYPASS_ACCESSTOKEN, true).set(BYPASS_RETRY, true), responseType: 'text' })
      .pipe(
        takeUntil(this.destroyed$)
      ).subscribe({
        next: (res) => {
          this._hasInternetAccess = true;
          this.noInternetAmountsAfterChange = 0;
          this.scheduleNextInternetCheck();
        },
        error: (err) => {
          this._hasInternetAccess = false;
          this.noInternetAmountsAfterChange++;
          this.loggerService.error(InternetConnectionStatusService.LOGGER_CLASSNAME, "checkInternetAccess", "Error checking internet access" +  err);
          this.scheduleNextInternetCheck();
        }
      });

    }



    private destroyed$ = new Subject<void>();
    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
        this.destroyed$ = null;
    }


}
